import { matchesTenantFilter, useTenantFilter } from '../../utils/tenantFilter'
import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchTags, deleteTag, fetchTagsUsage } from '../../redux/actions/tags'
import { Grid, makeStyles, Typography, Button } from '@material-ui/core'
import { TagModel } from '../../shared/types/tag'
import { useTranslation } from 'react-i18next'
import TagList from './tagList/TagList'
import TagCard from './tagCard/TagCard'
import { useCalcHeightToBottom } from '../../utils/ui'
import TagAddEditDialog from './tagAddEditDialog/TagAddEditDialog'
import { DeleteConfirmationDialog } from '../../shared/components/deleteConfirmationDialog'
import { fetchTenants } from '../../redux/actions/dashboard'

const useStyles = makeStyles((theme) => ({
    headerWrapper: {
        padding: theme.spacing(1),
    },
    actionButtonWrapper: {
        padding: theme.spacing(1, 0),
        textAlign: 'right',
    },
    listWrapper: {
        paddingTop: theme.spacing(2),
    },
    cardWrapper: {
        paddingTop: theme.spacing(2),
        width: '100%',
    },
}))

export default function Tags() {
    const classes = useStyles()
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const [selectedTag, setSelectedTag] = useState<TagModel>()
    
    // Lokálny stav vyhľadávania na frontende
    const [search, setSearch] = useState('')
    const [typeFilter, setTypeFilter] = useState<number[]>([])
    const tenantFilter = useTenantFilter()

    const [addEditDialogState, setAddEditDialogState] = useState({
        open: false,
        isAdd: true,
        onClose: (tag: TagModel) => { },
        initialModel: undefined as TagModel | undefined,
    })

    const [refreshTrigger, setRefreshTrigger] = useState(0)
    const [tagToDelete, setTagToDelete] = useState<TagModel>()

    const tagsState = useSelector((state: any) => state.tags.items.all)
    const tags = tagsState.value ?? []
    const deletedTag = useSelector((state: any) => state.tags.delete.value)
    const tagDeleteExecuted = useSelector((state: any) => state.tags.delete.executed)

    const tagCardHeightFill = useCalcHeightToBottom('tagsCardWrapper')

    const openAddDialog = () => {
        setAddEditDialogState({
            open: true,
            isAdd: true,
            onClose: onAddDialogClose,
            initialModel: undefined,
        })
    }

    const onAddDialogClose = (tag?: TagModel) => {
        setAddEditDialogState(prev => ({ ...prev, open: false }))
        if (tag) {
            setRefreshTrigger(prev => prev + 1)
        }
    }

    const onEditDialogClose = (tag?: TagModel) => {
        setAddEditDialogState(prev => ({ ...prev, open: false }))
        if (tag) {
            setRefreshTrigger(prev => prev + 1)
        }
    }

    const onTagSelected = useCallback((tag: TagModel) => {
        setSelectedTag(tag)
        dispatch(fetchTagsUsage(tag.id))
    }, [dispatch]);

    const onTagDelete = (tag: TagModel) => {
        setTagToDelete(tag)
    }

    const onTagEdit = (tag: TagModel) => {
        setAddEditDialogState({
            open: true,
            isAdd: false,
            onClose: onEditDialogClose,
            initialModel: tag,
        })
    }

    const handleRemoveTag = async () => {
        const tagToDeleteDefined = tagToDelete as TagModel
        await dispatch(deleteTag(tagToDeleteDefined))
        setRefreshTrigger(prev => prev + 1)
        setTagToDelete(undefined)
    }

    useEffect(() => {
        dispatch(fetchTags())
    }, [dispatch, refreshTrigger, deletedTag])

    useEffect(() => {
        dispatch(fetchTenants())
    }, [dispatch])

    // Filtrovanie prebieha lokálne tu na frontende
    const filteredTags = useMemo(() => {
        const searchLower = search.toLowerCase().trim();
        const tenantFiltered = tags.filter((tag: TagModel) => matchesTenantFilter(tag.tenantId, tenantFilter));
        const typeFiltered = typeFilter.length === 0
            ? tenantFiltered
            : tenantFiltered.filter((tag: TagModel) => typeFilter.includes(tag.type));
        if (!searchLower) return typeFiltered;

        return typeFiltered.filter((tag: TagModel) => 
            tag.name.toLowerCase().includes(searchLower)
        );
    }, [tags, search, typeFilter, tenantFilter]);

    // Nastavenie prvého / aktívneho tagu po zmene filtra
    useEffect(() => {
        if (tagsState.executed) {
            if (filteredTags && filteredTags.length > 0) {
                if (selectedTag != null) {
                    const tagToSelect = filteredTags.find((x: any) => x.id === selectedTag.id)
                    if (tagToSelect != null) {
                        setSelectedTag(tagToSelect)
                    } else {
                        onTagSelected(filteredTags[0])
                    }
                } else {
                    onTagSelected(filteredTags[0])
                }
            } else {
                setSelectedTag(undefined)
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredTags, tagsState.executed])

    return (
        <>
            <Grid container spacing={0}>
                <Grid item sm={2} className={classes.headerWrapper}>
                    <Typography align="left" variant="h3">
                        {t('tags.title')}
                    </Typography>
                </Grid>
                <Grid item sm={10} className={classes.actionButtonWrapper}>
                    <Button
                        onClick={openAddDialog}
                        variant="contained"
                        color="primary"
                    >
                        {t('tags.newTag')}
                    </Button>
                    {addEditDialogState.open && <TagAddEditDialog {...addEditDialogState} />}
                </Grid>
                <Grid item sm={4} className={classes.listWrapper}>
                    <TagList 
                        selectedTag={selectedTag} 
                        tags={filteredTags} 
                        search={search}
                        onSearchChange={setSearch}
                        typeFilter={typeFilter}
                        onTypeFilterChange={setTypeFilter}
                        onTagSelected={onTagSelected} 
                    />
                </Grid>
                <Grid item sm={8} className={classes.cardWrapper}>
                    <div id="tagsCardWrapper" ref={tagCardHeightFill}>
                        {selectedTag && (
                            <TagCard
                                tag={selectedTag}
                                onDelete={() => onTagDelete(selectedTag)}
                                onEdit={() => onTagEdit(selectedTag)}
                            />
                        )}
                    </div>
                </Grid>
            </Grid>
            <DeleteConfirmationDialog
                open={Boolean(tagToDelete)}
                onConfirm={handleRemoveTag}
                onCancel={() => setTagToDelete(undefined)}
                inProgress={!tagDeleteExecuted}
            />
        </>
    )
}